// src/components/AnalyticsDashboardPage.js

import React, { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine, faArrowLeft, faSpinner, faExclamationTriangle, faBuilding
} from '@fortawesome/free-solid-svg-icons';

import { functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../context/AuthContext';

import AnalyticsStatTiles from './AnalyticsStatTiles';
import AnalyticsPlatformCharts from './AnalyticsPlatformCharts';
import AnalyticsOrgHealthTable from './AnalyticsOrgHealthTable';

import './AnalyticsDashboardPage.css';

const getPlatformAnalyticsCallable = httpsCallable(functions, 'getPlatformAnalytics');

const AUTHORIZED_EMAIL = "jonstewart24@gmail.com";
const RANGE_OPTIONS = [7, 30, 90];

function AnalyticsDashboardPage() {
  const { currentUser, loadingAuth } = useAuth();

  const [days, setDays] = useState(30);
  const [platform, setPlatform] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedOrgId, setExpandedOrgId] = useState(null);

  useEffect(() => {
    if (loadingAuth || !currentUser) return;
    if (currentUser.email?.toLowerCase() !== AUTHORIZED_EMAIL.toLowerCase()) return;

    let cancelled = false;
    setLoading(true);
    setError(null);
    getPlatformAnalyticsCallable({ days })
      .then((result) => {
        if (cancelled) return;
        if (result.data.success) {
          setPlatform(result.data.platform);
          setOrgs(result.data.orgs || []);
        } else {
          setError(result.data.message || 'Failed to load analytics.');
        }
      })
      .catch((err) => {
        console.error('Error loading platform analytics:', err);
        if (!cancelled) setError(err.message || 'An error occurred while loading analytics.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [days, loadingAuth, currentUser]);

  const toggleOrg = (orgId) => {
    setExpandedOrgId(expandedOrgId === orgId ? null : orgId);
  };

  // --- Render ---

  if (loadingAuth) {
    return (
      <div className="analytics-page">
        <div className="analytics-loading">
          <div className="analytics-spinner"></div>
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  if (currentUser.email?.toLowerCase() !== AUTHORIZED_EMAIL.toLowerCase()) {
    return (
      <div className="analytics-page">
        <div className="analytics-header">
          <div className="container">
            <div className="analytics-header-content">
              <div className="analytics-header-left">
                <Link to="/dashboard" className="analytics-back-btn">
                  <FontAwesomeIcon icon={faArrowLeft} />
                </Link>
                <h1 className="analytics-title">Access Denied</h1>
              </div>
            </div>
          </div>
        </div>
        <div className="container">
          <div className="analytics-alert analytics-alert-danger">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <div>You do not have permission to view this page.</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-page">
      {/* Header */}
      <div className="analytics-header">
        <div className="container">
          <div className="analytics-header-content">
            <div className="analytics-header-left">
              <Link to="/dashboard" className="analytics-back-btn" title="Back to Dashboard">
                <FontAwesomeIcon icon={faArrowLeft} />
              </Link>
              <div>
                <h1 className="analytics-title">
                  <FontAwesomeIcon icon={faChartLine} style={{ marginRight: '0.5rem' }} />
                  Analytics Dashboard
                </h1>
                <p className="analytics-subtitle">Platform overview and per-organization health</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="analytics-content">
          {/* Error */}
          {error && (
            <div className="analytics-alert analytics-alert-danger">
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <div>{error}</div>
            </div>
          )}

          {/* Controls */}
          <div className="analytics-controls">
            <div className="analytics-range-group">
              <span className="analytics-range-label">Time Range:</span>
              <div className="analytics-range-buttons">
                {RANGE_OPTIONS.map((d) => (
                  <button
                    key={d}
                    className={`analytics-range-btn ${days === d ? 'active' : ''}`}
                    onClick={() => setDays(d)}
                    disabled={loading}
                  >
                    {d}d
                  </button>
                ))}
              </div>
            </div>
            {loading && (
              <span style={{ color: '#64748b', fontSize: '0.85rem' }}>
                <FontAwesomeIcon icon={faSpinner} spin /> Loading...
              </span>
            )}
          </div>

          {loading && !platform ? (
            <div className="analytics-loading">
              <div className="analytics-spinner"></div>
              <span>Loading platform analytics...</span>
            </div>
          ) : (
            <>
              {/* Platform overview */}
              <AnalyticsStatTiles platform={platform} days={days} />
              <AnalyticsPlatformCharts dailyActiveUsers={platform?.dailyActiveUsers} />

              {/* Org health */}
              <div className="analytics-section">
                <div className="analytics-section-header">
                  <h3 className="analytics-section-title">
                    <FontAwesomeIcon icon={faBuilding} />
                    Organization Health
                  </h3>
                  <span className="analytics-section-hint">Click a row for detailed charts</span>
                </div>
                <AnalyticsOrgHealthTable
                  orgs={orgs}
                  days={days}
                  expandedOrgId={expandedOrgId}
                  onToggle={toggleOrg}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AnalyticsDashboardPage;
